/**
 * Auto-Lock Inactivity Detection Hook
 *
 * Monitors user activity and triggers lock screen after configurable timeout
 * Tracks mouse movement, keyboard input, and touch events
 */

import { useState, useEffect, useCallback, useRef } from 'react';

export interface InactivityConfig {
  /** Timeout in milliseconds (default: 60000 = 1 minute) */
  timeout: number;
  /** Enable/disable auto-lock feature */
  enabled: boolean;
  /** Events to monitor for activity */
  events?: string[];
}

export interface InactivityLockState {
  /** Whether the screen is currently locked */
  isLocked: boolean;
  /** Last activity timestamp */
  lastActivity: Date | null;
  /** Manually lock the screen */
  lock: () => void;
  /** Unlock the screen (requires PIN verification) */
  unlock: () => void;
  /** Reset the inactivity timer */
  resetTimer: () => void;
}

const DEFAULT_EVENTS = [
  'mousedown',
  'mousemove',
  'keypress',
  'scroll',
  'touchstart',
  'click',
];

export const useInactivityLock = (
  config: InactivityConfig = { timeout: 60000, enabled: true }
): InactivityLockState => {
  const [isLocked, setIsLocked] = useState(false);
  const [lastActivity, setLastActivity] = useState<Date | null>(new Date());
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const eventsToMonitor = config.events || DEFAULT_EVENTS;

  const resetTimer = useCallback(() => {
    if (!config.enabled) return;

    // Clear existing timeout
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    // Update last activity timestamp
    setLastActivity(new Date());

    // Set new timeout
    timeoutIdRef.current = setTimeout(() => {
      setIsLocked(true);
      console.log('[Auto-Lock] Screen locked due to inactivity');
    }, config.timeout);
  }, [config.timeout, config.enabled]);

  const lock = useCallback(() => {
    setIsLocked(true);
    console.log('[Auto-Lock] Screen manually locked');
  }, []);

  const unlock = useCallback(() => {
    setIsLocked(false);
    setLastActivity(new Date());
    resetTimer();
    console.log('[Auto-Lock] Screen unlocked');
  }, [resetTimer]);

  // Monitor user activity
  useEffect(() => {
    if (!config.enabled || isLocked) return;

    const handleActivity = () => {
      resetTimer();
    };

    // Add event listeners
    eventsToMonitor.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Initialize timer
    resetTimer();

    // Cleanup
    return () => {
      eventsToMonitor.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [config.enabled, isLocked, eventsToMonitor, resetTimer]);

  return {
    isLocked,
    lastActivity,
    lock,
    unlock,
    resetTimer,
  };
};
